import React, { useState, useEffect } from 'react';
import { CreditCard, Download, Mail, Printer, Check, ChevronDown, ChevronUp, Info, Shield, Building2, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { getSourceData } from '../utils/sourceTracker';

/**
 * DigitalCard Component - Now supports multiple discount card networks
 * 
 * Based on RxGo's partner cards, users can choose from different PBM networks
 * which may offer different pricing at different pharmacies.
 */
const DigitalCard = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    subscribePromotions: true
  });
  const [cardGenerated, setCardGenerated] = useState(false);
  const [memberId, setMemberId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [showAllCards, setShowAllCards] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    secondary: '#0f172a',
    success: '#10b981',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  // Multiple card networks available through RxGo partnership
  // Different networks may offer better prices at different pharmacies
  const availableCards = [
    {
      name: 'Primary Card',
      description: 'Best for most pharmacies',
      bin: '610378',
      pcn: 'SC1',
      group: 'SRXG1222',
      color: '#2055df',
      recommended: true
    },
    {
      name: 'IntegrityRx',
      description: 'Great savings nationwide',
      bin: '610312',
      pcn: '01',
      group: 'NRXG1222',
      color: '#0891b2'
    },
    {
      name: 'AboveRx',
      description: 'Walmart & grocery stores',
      bin: '610186',
      pcn: 'ABOVERX',
      group: 'ABVRXG1222',
      color: '#7c3aed'
    },
    {
      name: 'Smart Script',
      description: 'Independent pharmacies',
      bin: '019819',
      pcn: 'SX',
      group: 'SRXG1222',
      color: '#059669'
    },
    {
      name: 'SaveRxNow',
      description: 'Chain pharmacies',
      bin: '013849',
      pcn: 'SAVE',
      group: 'VRXG1222',
      color: '#dc2626'
    }
  ];

  const selectedCard = availableCards[selectedCardIndex];

  // Reset form to allow new registration
  const handleUseDifferentEmail = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      zipCode: '',
      subscribePromotions: true
    });
    setCardGenerated(false);
    setMemberId('');
    setError('');
    setEmailSent(false);
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: formatPhoneNumber(value) }));
    } else if (name === 'zipCode') {
      const cleaned = value.replace(/\D/g, '').slice(0, 5);
      setFormData(prev => ({ ...prev, zipCode: cleaned }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    if (!formData.email || !formData.email.match(/^\S+@\S+\.\S+$/)) {
      setError('Please enter a valid email address');
      return false;
    }
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (formData.zipCode && formData.zipCode.length !== 5) {
      setError('Please enter a valid 5-digit ZIP code');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Get source attribution data
      const sourceData = getSourceData();

      const response = await fetch('/api/members/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          phone: formData.phone.replace(/\D/g, ''),
          source: sourceData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setMemberId(data.member.memberId);
      setCardGenerated(true);
      setEmailSent(true);

      // Mark visitor as converted
      try {
        await fetch('/api/visitors/convert', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sourceData.sessionId, memberId: data.member.memberId })
        });
      } catch (e) { /* non-critical */ }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCard = async () => {
    setSendingEmail(true);
    
    // Get the currently selected card details
    const currentCard = availableCards[selectedCardIndex];
    
    console.log('Sending email with card:', currentCard); // Debug log
    
    try {
      const response = await fetch('/api/members/email-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          cardInfo: {
            memberId,
            name: `${formData.firstName} ${formData.lastName}`.trim(),
            // Explicitly include card details
            bin: currentCard.bin,
            pcn: currentCard.pcn,
            group: currentCard.group,
            cardName: currentCard.name,
            cardColor: currentCard.color,
            cardDescription: currentCard.description
          }
        })
      });

      if (response.ok) {
        setEmailSent(true);
        alert(`Card sent to your email! (${currentCard.name})`);
      } else {
        throw new Error('Failed to send email');
      }
    } catch (err) {
      alert('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadCard = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');

    // Card background gradient
    const gradient = ctx.createLinearGradient(0, 0, 640, 400);
    gradient.addColorStop(0, selectedCard.color);
    gradient.addColorStop(1, colors.primaryDark);
    ctx.fillStyle = gradient;
    ctx.roundRect(0, 0, 640, 400, 20);
    ctx.fill();

    // White text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 28px Arial';
    ctx.fillText('Rx.ChatWithDr.com', 40, 60);
    ctx.font = '16px Arial';
    ctx.fillText('PRESCRIPTION DISCOUNT CARD', 40, 85);

    // Card name
    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fillText(selectedCard.name.toUpperCase(), 40, 115);

    // Card details
    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText('BIN', 40, 160);
    ctx.fillText('PCN', 200, 160);
    ctx.fillText('GROUP', 360, 160);

    ctx.font = 'bold 24px Arial';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(selectedCard.bin, 40, 190);
    ctx.fillText(selectedCard.pcn, 200, 190);
    ctx.fillText(selectedCard.group, 360, 190);

    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText('MEMBER ID', 40, 250);

    ctx.font = 'bold 24px Arial';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(memberId || 'CWDR000000000', 40, 280);

    if (formData.firstName || formData.lastName) {
      ctx.font = '14px Arial';
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillText('MEMBER NAME', 40, 330);
      ctx.font = 'bold 20px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(`${formData.firstName} ${formData.lastName}`.trim().toUpperCase(), 40, 360);
    }

    // Footer
    ctx.font = '12px Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText('Save up to 80% at 67,000+ pharmacies', 40, 385);

    // Download
    const link = document.createElement('a');
    link.download = `rx-chatwithdr-${selectedCard.name.toLowerCase().replace(/\s+/g, '-')}-card.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Card preview component
  const CardPreview = ({ card, size = 'normal', showDetails = true }) => (
    <div style={{
      background: `linear-gradient(135deg, ${card.color} 0%, ${colors.primaryDark} 100%)`,
      borderRadius: size === 'small' ? 12 : 16,
      padding: size === 'small' ? 16 : (isMobile ? 24 : 32),
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {card.recommended && (
        <div style={{
          position: 'absolute',
          top: 12,
          right: 12,
          background: 'rgba(255,255,255,0.2)',
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 10,
          fontWeight: 700
        }}>
          RECOMMENDED
        </div>
      )}
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: size === 'small' ? 12 : 20 }}>
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          borderRadius: 8,
          padding: '6px 12px',
          fontWeight: 800,
          fontSize: size === 'small' ? 16 : 20
        }}>
          Rx
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: size === 'small' ? 16 : 20 }}>ChatWithDr</div>
          {size !== 'small' && (
            <div style={{ fontSize: 12, opacity: 0.8 }}>PRESCRIPTION DISCOUNT CARD</div>
          )}
        </div>
      </div>

      {showDetails && (
        <>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: size === 'small' ? 8 : 16,
            marginBottom: size === 'small' ? 12 : 20
          }}>
            <div>
              <div style={{ fontSize: size === 'small' ? 10 : 11, opacity: 0.7, marginBottom: 2 }}>BIN</div>
              <div style={{ fontSize: size === 'small' ? 14 : 18, fontWeight: 700 }}>{card.bin}</div>
            </div>
            <div>
              <div style={{ fontSize: size === 'small' ? 10 : 11, opacity: 0.7, marginBottom: 2 }}>PCN</div>
              <div style={{ fontSize: size === 'small' ? 14 : 18, fontWeight: 700 }}>{card.pcn}</div>
            </div>
            <div>
              <div style={{ fontSize: size === 'small' ? 10 : 11, opacity: 0.7, marginBottom: 2 }}>GROUP</div>
              <div style={{ fontSize: size === 'small' ? 14 : 18, fontWeight: 700 }}>{card.group}</div>
            </div>
          </div>

          <div style={{ marginBottom: size === 'small' ? 8 : 12 }}>
            <div style={{ fontSize: size === 'small' ? 10 : 11, opacity: 0.7, marginBottom: 2 }}>MEMBER ID</div>
            <div style={{ fontSize: size === 'small' ? 14 : 18, fontWeight: 700 }}>{memberId || 'Enter details to generate'}</div>
          </div>

          {(formData.firstName || formData.lastName) && (
            <div>
              <div style={{ fontSize: size === 'small' ? 10 : 11, opacity: 0.7, marginBottom: 2 }}>MEMBER NAME</div>
              <div style={{ fontSize: size === 'small' ? 12 : 14, fontWeight: 600 }}>
                {`${formData.firstName} ${formData.lastName}`.trim().toUpperCase() || '—'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div style={{ 
      fontFamily: "'Inter', -apple-system, sans-serif",
      background: colors.bg,
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '48px 16px' : '80px 24px',
        marginTop: 0,
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: 1100,
          margin: '0 auto',
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 32 : 48,
          alignItems: 'center'
        }}>
          {/* Left - Text */}
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <h1 style={{
              fontSize: isMobile ? 28 : 48,
              fontWeight: 800,
              color: 'white',
              marginBottom: 14,
              lineHeight: 1.2
            }}>
              Get Your Free Discount Card
            </h1>
            <p style={{
              fontSize: isMobile ? 15 : 20,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.6,
              maxWidth: 500,
              margin: isMobile ? '0 auto' : 0
            }}>
              Save up to 80% on prescription medications at 67,000+ pharmacies nationwide
            </p>
          </div>

          {/* Right / Mobile Bottom - Animated Card */}
          <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : 30, y: isMobile ? 20 : 0 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              position: 'relative',
              marginTop: isMobile ? 32 : 0,
              paddingBottom: isMobile ? 24 : 0
            }}
          >
            <motion.div
              whileHover={{ rotate: 0, scale: 1.02 }}
              style={{
                background: `linear-gradient(135deg, ${selectedCard.color} 0%, ${colors.primaryDark} 100%)`,
                borderRadius: isMobile ? 16 : 20,
                padding: isMobile ? '24px 24px' : '32px 36px',
                boxShadow: '0 25px 60px -12px rgba(0,0,0,0.5)',
                transform: isMobile ? 'rotate(1deg)' : 'rotate(2deg)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                color: 'white',
                maxWidth: isMobile ? 340 : 'none',
                margin: isMobile ? '0 auto' : 0
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 20 : 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
                  <div style={{
                    width: isMobile ? 40 : 48,
                    height: isMobile ? 40 : 48,
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: isMobile ? 10 : 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: isMobile ? 16 : 20,
                    color: 'white'
                  }}>
                    Rx
                  </div>
                  <div>
                    <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700 }}>Rx Savings Card</div>
                    <div style={{ fontSize: isMobile ? 12 : 14, opacity: 0.7 }}>ChatWithDr.com</div>
                  </div>
                </div>
                <div style={{
                  background: '#fbbf24',
                  color: '#78350f',
                  fontSize: isMobile ? 11 : 12,
                  fontWeight: 700,
                  padding: isMobile ? '4px 10px' : '6px 14px',
                  borderRadius: 50
                }}>
                  FREE
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 14, marginBottom: isMobile ? 18 : 24 }}>
                {[
                  { label: 'BIN:', value: selectedCard.bin },
                  { label: 'PCN:', value: selectedCard.pcn },
                  { label: 'GROUP:', value: selectedCard.group }
                ].map((item) => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ opacity: 0.7, fontSize: isMobile ? 13 : 14 }}>{item.label}</span>
                    <span style={{ fontWeight: 700, fontSize: isMobile ? 14 : 16, fontFamily: 'monospace' }}>{item.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: isMobile ? 12 : 16 }}>
                <p style={{ opacity: 0.6, fontSize: isMobile ? 12 : 13, textAlign: 'center' }}>
                  Show this card at any participating pharmacy
                </p>
              </div>
            </motion.div>

            {/* Floating Badge - Save 80% */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              style={{
                position: 'absolute',
                top: isMobile ? -12 : -16,
                right: isMobile ? 8 : -8,
                background: '#10b981',
                color: 'white',
                padding: isMobile ? '8px 14px' : '10px 20px',
                borderRadius: 50,
                fontWeight: 700,
                fontSize: isMobile ? 13 : 16,
                boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
              }}
            >
              Save 80%
            </motion.div>

            {/* Floating Badge - Trusted */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              style={{
                position: 'absolute',
                bottom: isMobile ? 8 : -16,
                left: isMobile ? 8 : -8,
                background: 'white',
                padding: isMobile ? '8px 14px' : '12px 20px',
                borderRadius: isMobile ? 10 : 14,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <Star size={isMobile ? 14 : 18} style={{ color: '#fbbf24' }} fill="#fbbf24" />
              <span style={{ fontWeight: 700, color: '#0f172a', fontSize: isMobile ? 12 : 14 }}>Trusted by 1M+ Users</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ padding: isMobile ? '32px 16px' : '60px 24px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          {!cardGenerated ? (
            <div style={{
              background: 'white',
              borderRadius: 16,
              padding: isMobile ? 24 : 32,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: `1px solid ${colors.gray200}`
            }}>
              <h2 style={{ 
                marginBottom: 8, 
                fontSize: isMobile ? 20 : 24,
                fontWeight: 700,
                color: colors.gray900
              }}>
                Enter Your Information
              </h2>
              <p style={{ 
                marginBottom: 24, 
                color: colors.gray600,
                fontSize: isMobile ? 14 : 15
              }}>
                We'll create your personalized discount card and email it to you.
              </p>

              {error && (
                <div style={{
                  background: '#fef2f2',
                  color: '#dc2626',
                  padding: '12px 16px',
                  borderRadius: 8,
                  marginBottom: 20,
                  fontSize: 14
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Form fields remain the same */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                  gap: 16, 
                  marginBottom: 16 
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: colors.gray700 }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: `1px solid ${colors.gray200}`,
                        borderRadius: 8,
                        fontSize: 15,
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: colors.gray700 }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: `1px solid ${colors.gray200}`,
                        borderRadius: 8,
                        fontSize: 15,
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: colors.gray700 }}>
                    Email Address <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `1px solid ${colors.gray200}`,
                      borderRadius: 8,
                      fontSize: 15,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="john@example.com"
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: colors.gray700 }}>
                    Phone Number <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `1px solid ${colors.gray200}`,
                      borderRadius: 8,
                      fontSize: 15,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: colors.gray700 }}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `1px solid ${colors.gray200}`,
                      borderRadius: 8,
                      fontSize: 15,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="12345"
                    maxLength={5}
                  />
                </div>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: 10, 
                  marginBottom: 24,
                  padding: 16,
                  background: colors.gray100,
                  borderRadius: 10
                }}>
                  <input
                    type="checkbox"
                    id="subscribePromotions"
                    name="subscribePromotions"
                    checked={formData.subscribePromotions}
                    onChange={handleInputChange}
                    style={{
                      width: 20,
                      height: 20,
                      marginTop: 2,
                      cursor: 'pointer'
                    }}
                  />
                  <label 
                    htmlFor="subscribePromotions" 
                    style={{ 
                      fontSize: isMobile ? 13 : 14, 
                      color: colors.gray600, 
                      lineHeight: 1.5,
                      cursor: 'pointer'
                    }}
                  >
                    Yes, I'd like to receive promotional emails about prescription savings, 
                    health tips, and special offers from Rx.ChatWithDr and ChatWithDr.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    background: loading ? colors.gray400 : colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  <CreditCard size={20} />
                  {loading ? 'Creating Your Card...' : 'Get My Free Card'}
                </button>
              </form>
            </div>
          ) : (
            <div>
              {/* Success message */}
              <div style={{
                background: '#ecfdf5',
                color: '#065f46',
                padding: '16px 20px',
                borderRadius: 10,
                marginBottom: 24,
                fontSize: isMobile ? 14 : 15,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}>
                <Check size={20} />
                Your card has been created and sent to {formData.email}!
              </div>

              {/* Selected Card Preview */}
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: isMobile ? 20 : 24,
                marginBottom: 20,
                border: `1px solid ${colors.gray200}`
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: 16
                }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: colors.gray900 }}>
                    Your Discount Card
                  </h3>
                  <button
                    onClick={() => setShowAllCards(!showAllCards)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.primary,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    {showAllCards ? 'Show Less' : 'More Cards'}
                    {showAllCards ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>

                <CardPreview card={selectedCard} />

                {/* Action Buttons */}
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: 12, 
                  marginTop: 20
                }}>
                  <button
                    onClick={handleDownloadCard}
                    style={{
                      padding: '14px 20px',
                      background: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <Download size={18} /> Download
                  </button>
                  <button
                    onClick={handleEmailCard}
                    disabled={sendingEmail}
                    style={{
                      padding: '14px 20px',
                      background: 'white',
                      color: colors.primary,
                      border: `2px solid ${colors.primary}`,
                      borderRadius: 10,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: sendingEmail ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <Mail size={18} /> {sendingEmail ? 'Sending...' : 'Email'}
                  </button>
                  <button
                    onClick={() => window.print()}
                    style={{
                      padding: '14px 20px',
                      background: colors.gray100,
                      color: colors.gray800,
                      border: `2px solid ${colors.gray200}`,
                      borderRadius: 10,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <Printer size={18} /> Print
                  </button>
                </div>
              </div>

              {/* Other Card Options */}
              {showAllCards && (
                <div style={{
                  background: 'white',
                  borderRadius: 16,
                  padding: isMobile ? 20 : 24,
                  marginBottom: 20,
                  border: `1px solid ${colors.gray200}`
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 10, 
                    marginBottom: 16,
                    padding: 12,
                    background: `${colors.primary}10`,
                    borderRadius: 8
                  }}>
                    <Info size={18} color={colors.primary} style={{ flexShrink: 0, marginTop: 2 }} />
                    <p style={{ fontSize: 13, color: colors.gray600, lineHeight: 1.5 }}>
                      Different cards may offer better prices at different pharmacies. 
                      When you search for a drug, we'll automatically show you the best price 
                      regardless of which network it uses.
                    </p>
                  </div>

                  <h4 style={{ 
                    fontSize: 16, 
                    fontWeight: 600, 
                    color: colors.gray900, 
                    marginBottom: 16 
                  }}>
                    Alternative Cards
                  </h4>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {availableCards.map((card, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedCardIndex(index)}
                        style={{
                          border: selectedCardIndex === index 
                            ? `2px solid ${card.color}` 
                            : `1px solid ${colors.gray200}`,
                          borderRadius: 12,
                          padding: 16,
                          cursor: 'pointer',
                          background: selectedCardIndex === index ? `${card.color}08` : 'white',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 8, 
                              marginBottom: 4 
                            }}>
                              <div style={{
                                width: 12,
                                height: 12,
                                borderRadius: 3,
                                background: card.color
                              }} />
                              <span style={{ fontWeight: 600, color: colors.gray900 }}>
                                {card.name}
                              </span>
                              {card.recommended && (
                                <span style={{
                                  background: colors.success,
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  fontSize: 10,
                                  fontWeight: 600
                                }}>
                                  RECOMMENDED
                                </span>
                              )}
                            </div>
                            <p style={{ fontSize: 13, color: colors.gray500 }}>
                              {card.description}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right', fontSize: 12, color: colors.gray500 }}>
                            <div>BIN: {card.bin}</div>
                            <div>PCN: {card.pcn}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Need a Prescription? Promo */}
              <div style={{
                background: `linear-gradient(135deg, ${colors.success}15, ${colors.primary}10)`,
                border: `1px solid ${colors.success}30`,
                borderRadius: 12,
                padding: isMobile ? 20 : 24,
                marginBottom: 20
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  justifyContent: 'space-between',
                  gap: 16
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 32 }}>🩺</span>
                    <div>
                      <p style={{ 
                        fontWeight: 600, 
                        color: colors.gray900, 
                        marginBottom: 4,
                        fontSize: isMobile ? 16 : 18
                      }}>
                        Need a Prescription?
                      </p>
                      <p style={{ 
                        color: colors.gray600, 
                        fontSize: isMobile ? 14 : 15 
                      }}>
                        Chat with a licensed doctor online for just <span style={{ fontWeight: 700, color: colors.success }}>$39.99</span>
                      </p>
                    </div>
                  </div>
                  <a
                    href="https://chatwithdr.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '12px 24px',
                      background: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      width: isMobile ? '100%' : 'auto',
                      justifyContent: 'center'
                    }}
                  >
                    💬 Start Chat
                  </a>
                </div>
              </div>

              {/* Use different email */}
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={handleUseDifferentEmail}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.primary,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Use a different email
                </button>
              </div>
            </div>
          )}

          {/* How to Use */}
          <div style={{
            background: 'white',
            borderRadius: 16,
            padding: isMobile ? 24 : 32,
            marginTop: 24,
            border: `1px solid ${colors.gray200}`
          }}>
            <h3 style={{ 
              marginBottom: 20, 
              fontSize: isMobile ? 18 : 20,
              fontWeight: 600,
              color: colors.gray900
            }}>
              How to Use Your Card
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { icon: '🔍', title: 'Search for your medication', desc: 'Find the best price near you' },
                { icon: '📍', title: 'Choose a pharmacy', desc: 'See prices at pharmacies nearby' },
                { icon: '🎟️', title: 'Get your coupon', desc: 'Show the card details to your pharmacist' },
                { icon: '💰', title: 'Save up to 80%', desc: 'Pay the discounted price at checkout' }
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: colors.gray100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    flexShrink: 0
                  }}>
                    {step.icon}
                  </div>
                  <div>
                    <p style={{ fontWeight: 600, color: colors.gray900, marginBottom: 2 }}>{step.title}</p>
                    <p style={{ color: colors.gray500, fontSize: 14 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: 24,
              padding: 16,
              background: colors.gray100,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12
            }}>
              <Shield size={20} color={colors.primary} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontWeight: 600, color: colors.gray900, marginBottom: 4 }}>
                  Not Insurance
                </p>
                <p style={{ fontSize: 13, color: colors.gray600, lineHeight: 1.5 }}>
                  This card is not insurance. Discounts are only available at participating pharmacies. 
                  Accepted at CVS, Walgreens, Walmart, Rite Aid, and 67,000+ pharmacies nationwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DigitalCard;